import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { TranslationContext } from '@strava/container-context';
import EmbedModal from '@strava/embed-modal';
import I18n from 'utils/I18n';
import {
  Dropdown,
  DropdownItem,
  DropdownLink,
  DropdownList
} from '@strava/ui/Dropdown';
import { useFeedEntityContext } from '../FeedEntityContext';
import EntityDeleteAlertDialog from '../EntityDeleteAlertDialog';

import styles from './styles.scss';

const EntryActions = ({
  entityId,
  shareable,
  twitterUrl,
  embedDropdownEnabled,
  entityParentType,
  entityParentId,
  isDeletable,
  extraEntityActions
}) => {
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);
  const { feedEntityState } = useFeedEntityContext();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const toggleEmbedModalOpen = (event) => {
    event.preventDefault();
    setIsEmbedModalOpen(!isEmbedModalOpen);
  };

  function facebookShareUrl() {
    const activityUrl = `https://www.strava.com/activities/${entityId}`;
    // Facebook will error if the URL is not publicly available (like with localhost)
    // In which case, you can test using a stubbed route like:
    // const pageUrl = 'https://www.strava.com/activitiy/22481075';
    return `https://www.facebook.com/sharer.php?u=${activityUrl}`;
  }

  function openShareWindow() {
    const left = (window.screen.width - 570) / 2;
    const top = (window.screen.height - 570) / 2;
    const params = `menubar=no,toolbar=no,status=no,width=570,height=570,top=${top},left=${left}`;
    // Setting 'params' to an empty string will launch
    // content in a new tab or window rather than a pop-up.
    // params = "";
    window.open(facebookShareUrl(), 'NewWindow', params);
  }

  return (
    <div className={styles.entryActions} data-testid="entry_actions_container">
      {(shareable || isDeletable || extraEntityActions) && (
        <div data-testid="shareable">
          <Dropdown
            text=""
            onClick={() => {
              // old analytics call from when this component was called
              // the "Share Dropdown", left for legacy compatiblity reasons
              feedEntityState.trackAnalytics({
                action: 'click',
                element: 'share'
              });

              // new analytics call.
              feedEntityState.trackAnalytics({
                action: 'click',
                element: 'entity_actions',
                properties: {
                  entity_id: entityId,
                  entity_parent_id: entityParentId
                }
              });
            }}
            className={`${styles.dropdown}`}
          >
            <DropdownList>
              {shareable && (
                <DropdownItem
                  href="#"
                  onSelect={openShareWindow}
                  className={`${styles.dropdownOption}`}
                >
                  Facebook
                </DropdownItem>
              )}
              {shareable && (
                <DropdownLink
                  href={twitterUrl}
                  className={`${styles.dropdownOption}`}
                >
                  Twitter
                </DropdownLink>
              )}
              {embedDropdownEnabled && shareable && (
                <DropdownLink
                  href="#"
                  onClick={toggleEmbedModalOpen}
                  className={`${styles.dropdownOption}`}
                >
                  {I18n.t('templates.share.embed.get_code')}
                  <TranslationContext.Provider
                    value={{
                      t: (key, props = {}) => I18n.t(key, props),
                      getLanguage: () => I18n.language()
                    }}
                  >
                    <EmbedModal
                      embedType="activity"
                      embedId={entityId}
                      isOpen={isEmbedModalOpen}
                      onDismiss={toggleEmbedModalOpen}
                    />
                  </TranslationContext.Provider>
                </DropdownLink>
              )}
              {isDeletable && (
                <DropdownItem
                  data-testid="delete-btn"
                  onClick={() => setIsDeleteModalOpen(true)}
                >
                  {I18n.t('templates.generic.delete')}
                </DropdownItem>
              )}
              {extraEntityActions?.map(([node, props], i) => (
                <DropdownItem
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className={styles.dropdownOption}
                  {...props}
                >
                  {node}
                </DropdownItem>
              ))}
            </DropdownList>
          </Dropdown>
        </div>
      )}
      {isDeletable && (
        <EntityDeleteAlertDialog
          entityId={entityId}
          isDeleteModalOpen={isDeleteModalOpen}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          entityParentType={entityParentType}
          clubOrAthleteId={entityParentId}
        />
      )}
    </div>
  );
};

EntryActions.propTypes = {
  entityId: PropTypes.string.isRequired,
  shareable: PropTypes.bool.isRequired,
  twitterUrl: PropTypes.string.isRequired,
  embedDropdownEnabled: PropTypes.bool.isRequired,
  entityParentType: PropTypes.string.isRequired,
  entityParentId: PropTypes.number.isRequired,
  extraEntityActions: PropTypes.arrayOf(PropTypes.shape({})),
  isDeletable: PropTypes.bool
};

EntryActions.defaultProps = {
  isDeletable: false,
  extraEntityActions: undefined
};

export default EntryActions;
